import clsx from 'clsx';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  RESOLUTION_LABEL,
  cnDate,
  fmtDateWeek,
  fmtListTime,
  fmtMonthDay,
  fmtRelativeDay,
  type AnomalyDTO,
  type AssignmentDTO,
  type ExitRequestDTO,
  type FollowUpDTO,
  type HandoverDTO,
  type InboxDTO,
  type JoinRequestDTO,
  type MemberDTO,
  type MemberMessageDTO,
  type MentionDTO,
} from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { errorText, post } from '../../lib/api';
import { timeCN, todayCN } from '../../lib/clock';
import { useHotkeys } from '../../lib/hotkeys';
import { useLayout } from '../../lib/media';
import { queryClient, useHandoverActions, useInbox, useInboxActions, useMe, useMembers, useMessageRead } from '../../lib/queries';
import { Icon, MoreIcon, StatusIcon } from '../../ui/icons';
import { AIAvatar, Avatar, Button, EmptyState, GroupLabel, Hint, IconButton, Kbd, Kbds, Message, Quote, Skeleton } from '../../ui/primitives';
import { RichText } from '../../ui/RichText';
import { AskModal, AssignModal, RejectExitModal, ResolveModal, ReturnModal, TransferModal } from './modals';
import '../report/report.css';
import './inbox.css';

type Entry =
  | { kind: 'anomaly'; id: string; a: AnomalyDTO }
  | { kind: 'assignment'; id: string; x: AssignmentDTO }
  | { kind: 'exit'; id: string; e: ExitRequestDTO }
  | { kind: 'followup'; id: string; f: FollowUpDTO }
  | { kind: 'join'; id: string; j: JoinRequestDTO }
  | { kind: 'mention'; id: string; m: MentionDTO }
  | { kind: 'handover'; id: string; h: HandoverDTO }
  | { kind: 'message'; id: string; msg: MemberMessageDTO };

function entriesOf(inbox: InboxDTO): Entry[] {
  const open = inbox.anomalies.filter((a) => a.status !== 'closed');
  const closed = inbox.anomalies.filter((a) => a.status === 'closed');
  return [
    ...open.map((a) => ({ kind: 'anomaly' as const, id: a.id, a })),
    ...closed.map((a) => ({ kind: 'anomaly' as const, id: a.id, a })),
    ...inbox.assignedToMe.map((x) => ({ kind: 'assignment' as const, id: x.id, x })),
    ...inbox.exitRequests.map((e) => ({ kind: 'exit' as const, id: e.id, e })),
    ...inbox.handovers.map((h) => ({ kind: 'handover' as const, id: h.id, h })),
    ...inbox.followUpsToMe.map((f) => ({ kind: 'followup' as const, id: f.id, f })),
    ...inbox.joinRequests.map((j) => ({ kind: 'join' as const, id: j.id, j })),
    ...inbox.mentions.map((m) => ({ kind: 'mention' as const, id: m.id, m })),
    ...inbox.messages.map((msg) => ({ kind: 'message' as const, id: msg.id, msg })),
  ];
}

const isDone = (en: Entry) =>
  (en.kind === 'anomaly' && en.a.status === 'closed') ||
  (en.kind === 'assignment' && en.x.status !== 'open') ||
  (en.kind === 'exit' && en.e.status !== 'pending');

type ModalState =
  | null
  | { type: 'ask'; a: AnomalyDTO; text?: string }
  | { type: 'assign'; a: AnomalyDTO; who?: string; ask?: string }
  | { type: 'resolve'; a: AnomalyDTO }
  | { type: 'return'; x: AssignmentDTO }
  | { type: 'transfer'; x: AssignmentDTO }
  | { type: 'reject'; e: ExitRequestDTO };

export function InboxPage() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'mobile';
  const { data: inbox, isLoading, error } = useInbox();
  const { data: members = [] } = useMembers();
  const [modal, setModal] = useState<ModalState>(null);
  const entries = useMemo(() => (inbox ? entriesOf(inbox) : []), [inbox]);
  // 全部处理完时右侧显示空状态（设计稿 13d），列表保留复查日期
  const selected =
    entries.find((e) => e.id === routeId) ?? (mobile ? undefined : entries.find((e) => !isDone(e) && e.kind !== 'mention' && e.kind !== 'message'));
  const idx = selected ? entries.indexOf(selected) : -1;

  const go = (i: number) => {
    const en = entries[Math.max(0, Math.min(entries.length - 1, idx < 0 && i < 0 ? 0 : i))];
    if (en) navigate(`/inbox/${en.id}`, { replace: true });
  };

  useHotkeys(
    {
      ArrowDown: () => go(idx + 1),
      ArrowUp: () => go(idx - 1),
      j: () => go(idx + 1),
      k: () => go(idx - 1),
      r: () => selected?.kind === 'anomaly' && selected.a.status !== 'closed' && setModal({ type: 'ask', a: selected.a }),
      a: () => selected?.kind === 'anomaly' && selected.a.status !== 'closed' && setModal({ type: 'assign', a: selected.a }),
      e: () => selected?.kind === 'anomaly' && selected.a.status !== 'closed' && setModal({ type: 'resolve', a: selected.a }),
      t: () => selected?.kind === 'assignment' && selected.x.status === 'open' && setModal({ type: 'transfer', x: selected.x }),
    },
    !mobile,
  );

  const modals = (
    <>
      {modal?.type === 'ask' && <AskModal anomaly={modal.a} initialText={modal.text} onClose={() => setModal(null)} />}
      {modal?.type === 'assign' && <AssignModal anomaly={modal.a} initialAssigneeName={modal.who} initialAsk={modal.ask} onClose={() => setModal(null)} />}
      {modal?.type === 'resolve' && <ResolveModal anomaly={modal.a} onClose={() => setModal(null)} />}
      {modal?.type === 'return' && <ReturnModal assignment={modal.x} onClose={() => setModal(null)} />}
      {modal?.type === 'transfer' && <TransferModal assignment={modal.x} onClose={() => setModal(null)} />}
      {modal?.type === 'reject' && <RejectExitModal request={modal.e} onClose={() => setModal(null)} />}
    </>
  );

  if (isLoading || !inbox) {
    return (
      <div style={{ padding: 24 }} aria-busy="true">
        {error ? <EmptyState icon="wifioff" title="收件箱没有加载出来" desc={errorText(error)} /> : <Skeleton h={14} w={240} />}
      </div>
    );
  }

  const detail = selected ? (
    <Detail entry={selected} members={members} setModal={setModal} position={`${idx + 1} / ${entries.length}`} onMove={(d) => go(idx + d)} mobile={mobile} />
  ) : (
    <AllDone inbox={inbox} />
  );

  if (mobile) {
    return (
      <>
        {routeId && selected ? (
          <div className="col" style={{ flex: 1, minHeight: 0 }}>
            <button type="button" className="ibm-back" onClick={() => navigate('/inbox')}>
              <Icon name="chevL" size={18} color="var(--text-3)" />
              <span>收件箱</span>
            </button>
            {detail}
          </div>
        ) : (
          <MobileList inbox={inbox} entries={entries} />
        )}
        {modals}
      </>
    );
  }

  return (
    <div className="ib">
      <div className="ib-list">
        <div className="ib-list-head">
          <span>收件箱</span>
          <span className="row" style={{ gap: 2 }}>
            <IconButton name="refresh" label="刷新" onClick={() => location.reload()} />
          </span>
        </div>
        <Summary inbox={inbox} />
        <div className="ib-rows">
          {entries.length === 0 && <div className="t-meta" style={{ padding: '12px' }}>暂时没有需要你处理的事</div>}
          <EntryGroups entries={entries} selectedId={selected?.id} onPick={(id) => navigate(`/inbox/${id}`, { replace: true })} />
        </div>
        <div className="ib-list-foot">
          <Hint keys={['↑', '↓']}>切换</Hint>
          {selected?.kind === 'anomaly' && <Hint keys={['R']}>追问</Hint>}
          {selected?.kind === 'anomaly' && <Hint keys={['E']}>标记已处理</Hint>}
        </div>
      </div>
      <div className="ib-detail">{detail}</div>
      {modals}
    </div>
  );
}

function Summary({ inbox }: { inbox: InboxDTO }) {
  const total = inbox.counts.pending + inbox.counts.handled;
  return (
    <div className="ib-summary">
      <span className="row" style={{ gap: 6 }}>
        {inbox.counts.pending > 0 ? (
          <>
            <span className="c2">今天 {inbox.counts.pending} 条需要处理</span>
            {inbox.counts.handled > 0 && (
              <>
                <span>·</span>
                <span>{inbox.counts.handled} 条已处理</span>
              </>
            )}
          </>
        ) : total > 0 ? (
          <span className="c2">今天 {total} 条 · 全部已处理</span>
        ) : (
          <span className="c2">今天没有需要处理的事</span>
        )}
      </span>
      {inbox.generatedAt && <span>{timeCN(inbox.generatedAt)} 更新</span>}
    </div>
  );
}

function EntryGroups({ entries, selectedId, onPick }: { entries: Entry[]; selectedId?: string; onPick: (id: string) => void }) {
  const today = todayCN();
  const anomalies = entries.filter((e) => e.kind === 'anomaly');
  const groups: { name: string; list: Entry[] }[] = [
    { name: '', list: anomalies },
    { name: '指派给我', list: entries.filter((e) => e.kind === 'assignment' && !isDone(e)) },
    { name: '需要你确认', list: entries.filter((e) => (e.kind === 'exit' || e.kind === 'handover') && !isDone(e)) },
    { name: '追问', list: entries.filter((e) => e.kind === 'followup') },
    { name: '申请加入', list: entries.filter((e) => e.kind === 'join') },
    { name: '提到我', list: entries.filter((e) => e.kind === 'mention') },
    { name: '消息', list: entries.filter((e) => e.kind === 'message') },
    { name: '已处理', list: entries.filter((e) => e.kind !== 'anomaly' && isDone(e)) },
  ];
  const showHeaders = groups.filter((g) => g.list.length).length > 1 || anomalies.length === 0;
  return (
    <>
      {groups.map((g, gi) =>
        g.list.length === 0 ? null : (
          <div key={gi}>
            {showHeaders && g.name && (
              <div className="ib-group">
                <span className="name">{g.name}</span>
                <span>{g.list.length}</span>
              </div>
            )}
            {g.list.map((en, i) => {
              const prev = g.list[i - 1];
              const sep = g.name === '' && prev && !isDone(prev) && isDone(en);
              return (
                <div key={en.id}>
                  {sep && <div className="ib-sep" />}
                  <Row entry={en} selected={en.id === selectedId} onClick={() => onPick(en.id)} today={today} />
                </div>
              );
            })}
          </div>
        ),
      )}
    </>
  );
}

function entryIcon(en: Entry) {
  switch (en.kind) {
    case 'anomaly':
      return <StatusIcon status={en.a.status === 'closed' ? 'done' : en.a.type === 'late_start' ? 'todo' : en.a.type === 'said_not_done' ? 'cancelled' : 'blocked'} dim={en.a.status === 'closed'} />;
    case 'assignment':
      return <StatusIcon status={en.x.status === 'open' ? 'progress' : 'done'} dim={en.x.status !== 'open'} />;
    case 'exit':
      return <Icon name="out" size={14} color="var(--text-3)" />;
    case 'followup':
      return en.f.kind === 'ai' ? <AIAvatar size={14} /> : <Avatar initial={en.f.fromInitial} seed={en.f.fromName} size={16} />;
    case 'join':
      return <Icon name="user" size={14} color="var(--text-3)" />;
    case 'mention':
      return <Avatar initial={en.m.author.initial} seed={en.m.author.name} size={16} />;
    case 'handover':
      return <Icon name="refresh" size={14} color="var(--text-3)" />;
    case 'message':
      return <Avatar initial={en.msg.from.initial} seed={en.msg.from.name} size={16} />;
  }
}

function entryTitle(en: Entry) {
  switch (en.kind) {
    case 'anomaly':
      return en.a.title;
    case 'assignment':
      return en.x.ask;
    case 'exit':
      return `${en.e.memberName}申请退出 ${en.e.projectCode} ${en.e.projectName}`;
    case 'followup':
      return en.f.question;
    case 'join':
      return `${en.j.email} 申请加入工作区`;
    case 'mention':
      // 开头的“@某人”对收件人是多余的，列表里去掉，详情里保留原文
      return en.m.text.replace(/^(@\S+\s*)+/, '') || en.m.text;
    case 'handover':
      return `接手 ${en.h.projectCode} ${en.h.projectName}`;
    case 'message':
      return en.msg.kind === 'one_on_one' && en.msg.meetingAt
        ? `${en.msg.from.name}约你 ${fmtMonthDay(cnDate(new Date(en.msg.meetingAt)))} ${timeCN(en.msg.meetingAt)} 聊聊`
        : en.msg.text;
  }
}

function Row({ entry: en, selected, onClick, today }: { entry: Entry; selected: boolean; onClick: () => void; today: string }) {
  const done = isDone(en);
  let tag: string | null = null;
  let who = '';
  let time = '';
  if (en.kind === 'anomaly') {
    const a = en.a;
    if (a.status === 'replied') tag = '已回复';
    else if (a.status === 'asked') tag = '已追问';
    else if (a.status === 'assigned') tag = `已指派${a.assignment ? `给${a.assignment.assigneeName}` : ''}`;
    else if (a.status === 'closed' && a.resolution?.kind === 'following' && a.resolution.recheckAt) tag = `${fmtMonthDay(a.resolution.recheckAt)}复查`;
    who = a.involvedNames.join(' · ');
    time = fmtListTime(a.generatedAt, today);
  } else if (en.kind === 'assignment') {
    who = en.x.assignerName;
    time = fmtListTime(en.x.createdAt, today);
    if (en.x.status === 'open') tag = `${fmtRelativeDay(cnDate(new Date(en.x.dueAt)), today)} ${timeCN(en.x.dueAt)}`;
  } else if (en.kind === 'exit') {
    who = en.e.memberName;
    time = fmtListTime(en.e.createdAt, today);
  } else if (en.kind === 'followup') {
    who = en.f.fromName;
    time = fmtListTime(en.f.askedAt, today);
  } else if (en.kind === 'mention') {
    tag = en.m.projectCode;
    who = en.m.author.name;
    time = fmtListTime(en.m.createdAt, today);
  } else if (en.kind === 'handover') {
    tag = '离职交接';
    who = en.h.memberName;
    time = fmtListTime(en.h.createdAt, today);
  } else if (en.kind === 'message') {
    tag = en.msg.kind === 'thanks' ? '感谢' : '1:1';
    who = en.msg.from.name;
    time = fmtListTime(en.msg.createdAt, today);
  } else {
    time = fmtListTime(en.j.createdAt, today);
  }
  return (
    <button type="button" className={clsx('ib-row', selected && 'sel', done && 'done')} onClick={onClick}>
      {entryIcon(en)}
      <span className="title">{entryTitle(en)}</span>
      {tag && <span className="tag">{tag}</span>}
      <span className="who">{who}</span>
      <span className="time">{time}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// 详情
// ---------------------------------------------------------------------------

function Detail({
  entry,
  members,
  setModal,
  position,
  onMove,
  mobile,
}: {
  entry: Entry;
  members: MemberDTO[];
  setModal: (m: ModalState) => void;
  position: string;
  onMove: (d: number) => void;
  mobile: boolean;
}) {
  const crumb =
    entry.kind === 'anomaly'
      ? `${entry.a.projectCode} ${entry.a.projectName}`
      : entry.kind === 'assignment'
        ? '指派给我'
        : entry.kind === 'exit' || entry.kind === 'handover'
          ? '需要你确认'
          : entry.kind === 'message'
            ? '消息'
          : entry.kind === 'join'
            ? '申请加入'
            : entry.kind === 'mention'
              ? '提到我'
              : entry.f.kind === 'ai'
              ? 'AI 追问'
              : `${entry.f.fromName}的追问`;
  const body =
    entry.kind === 'anomaly' ? (
      <AnomalyDetail a={entry.a} members={members} setModal={setModal} mobile={mobile} />
    ) : entry.kind === 'assignment' ? (
      <AssignmentDetail x={entry.x} setModal={setModal} mobile={mobile} />
    ) : entry.kind === 'exit' ? (
      <ExitDetail e={entry.e} members={members} setModal={setModal} mobile={mobile} />
    ) : entry.kind === 'join' ? (
      <JoinDetail j={entry.j} mobile={mobile} />
    ) : entry.kind === 'mention' ? (
      <MentionDetail m={entry.m} mobile={mobile} />
    ) : entry.kind === 'handover' ? (
      <HandoverDetail h={entry.h} mobile={mobile} />
    ) : entry.kind === 'message' ? (
      <MessageDetail msg={entry.msg} mobile={mobile} />
    ) : (
      <FollowUpDetail f={entry.f} mobile={mobile} />
    );
  return (
    <>
      {!mobile && (
        <TopBar crumbs={['收件箱', crumb]}>
          <span className="t-meta" style={{ marginRight: 10 }}>
            {position}
          </span>
          <IconButton name="chevU" label="上一条" onClick={() => onMove(-1)} />
          <IconButton name="chevD" label="下一条" onClick={() => onMove(1)} />
          <button type="button" className="wd-btn icon" aria-label="更多">
            <MoreIcon />
          </button>
        </TopBar>
      )}
      {body}
    </>
  );
}

function Actions({ children, hints, mobile }: { children: ReactNode; hints?: ReactNode; mobile: boolean }) {
  return mobile ? <div className="ibm-actions">{children}</div> : (
    <div className="ib-actions">
      {children}
      {hints && <div className="hints">{hints}</div>}
    </div>
  );
}

function Body({ children, mobile }: { children: ReactNode; mobile: boolean }) {
  return mobile ? (
    <div className="ibm-detail" style={{ flex: 1, overflow: 'auto' }}>
      {children}
    </div>
  ) : (
    <div className="ib-body">
      <div className="in">{children}</div>
    </div>
  );
}

function AnomalyDetail({ a, members, setModal, mobile }: { a: AnomalyDTO; members: MemberDTO[]; setModal: (m: ModalState) => void; mobile: boolean }) {
  const today = todayCN();
  const replied = a.status === 'replied';
  const closed = a.status === 'closed';
  const [showEvidence, setShowEvidence] = useState(!replied);
  const [showTimeline, setShowTimeline] = useState(!replied);
  useEffect(() => {
    setShowEvidence(a.status !== 'replied');
    setShowTimeline(a.status !== 'replied');
  }, [a.id, a.status]);

  const lastAnswer = [...a.followUps].reverse().find((f) => f.answer);
  const typeColor = a.type === 'late_start' || a.type === 'said_not_done' ? 'var(--text-3)' : 'var(--status-blocked)';

  const onSuggestion = (s: string) => {
    const named = members.find((m) => m.id !== a.ownerId && s.includes(m.name));
    if (named) setModal({ type: 'assign', a, who: named.name, ask: s.replace(/^请/, '') });
    else setModal({ type: 'ask', a, text: s });
  };

  return (
    <>
      <Body mobile={mobile}>
        <div className="ib-kind">
          {closed ? <StatusIcon status="done" dim /> : <StatusIcon status={a.type === 'late_start' ? 'todo' : 'blocked'} />}
          <span style={{ color: closed ? 'var(--text-3)' : typeColor }}>{a.typeLabel}</span>
          <span>·</span>
          <span>{a.projectCode}</span>
          <span>·</span>
          <span>负责人 {a.ownerName}</span>
          {replied && lastAnswer && (
            <>
              <span>·</span>
              <span className="c2">{lastAnswer.toName}已回复</span>
            </>
          )}
        </div>
        <div className="t-page-title ib-title">{a.title}</div>
        <div className="ib-judge">
          <AIAvatar size={16} />
          <div className="text">
            {replied && lastAnswer ? `有人跟进了：${lastAnswer.toName}回复“${lastAnswer.answer}”。` : a.judgement}
          </div>
        </div>

        {closed && a.resolution && (
          <div className="ib-sec">
            <GroupLabel>处理结论</GroupLabel>
            <div className="row" style={{ gap: 8, fontSize: 13 }}>
              <StatusIcon status="done" />
              <span className="c1">{RESOLUTION_LABEL[a.resolution.kind]}</span>
              {a.resolution.recheckAt && <span className="t-meta">· {fmtMonthDay(a.resolution.recheckAt)} 复查</span>}
            </div>
            {a.resolution.note && (
              <div className="t-body" style={{ marginTop: 6 }}>
                {a.resolution.note}
              </div>
            )}
          </div>
        )}

        {a.followUps.length > 0 && (
          <div className="ib-sec">
            <GroupLabel>追问 · {fmtMonthDay(cnDate(new Date(a.followUps[0].askedAt)))}</GroupLabel>
            <div className="ib-thread">
              {a.followUps.map((f) => (
                <div key={f.id} className="col" style={{ gap: 14 }}>
                  <Message
                    avatar={<Avatar initial={f.fromInitial} seed={f.fromName} size={20} />}
                    name={f.fromName}
                    meta={`${timeCN(f.askedAt)}${f.ccNames.length ? ` · 抄送 ${f.ccNames.join('、')}` : ''}`}
                  >
                    {f.question}
                  </Message>
                  {f.answer ? (
                    <Message
                      avatar={<Avatar initial={members.find((m) => m.id === f.toId)?.initial ?? '?'} seed={members.find((m) => m.id === f.toId)?.name} size={20} />}
                      name={f.toName}
                      meta={`${f.answeredAt ? timeCN(f.answeredAt) : ''}${f.answerChannel === 'wechat' ? ' · 来自微信' : ''}`}
                    >
                      {f.answer}
                    </Message>
                  ) : (
                    <div className="t-meta" style={{ marginLeft: 32 }}>
                      等待 {f.toName} 回复
                    </div>
                  )}
                </div>
              ))}
              {lastAnswer && <div className="t-meta" style={{ marginLeft: 32 }}>已写入 {a.projectCode} 今天的进展</div>}
            </div>
          </div>
        )}

        {a.assignment && (
          <div className="ib-sec">
            <GroupLabel>指派</GroupLabel>
            <Message avatar={<Avatar initial={a.assignment.assignerInitial} seed={a.assignment.assignerName} size={20} />} name={`${a.assignment.assignerName} → ${a.assignment.assigneeName}`} meta={`期限 ${fmtRelativeDay(cnDate(new Date(a.assignment.dueAt)), today)} ${timeCN(a.assignment.dueAt)}`}>
              {a.assignment.ask}
            </Message>
            {a.assignment.reply && (
              <div style={{ marginTop: 12 }}>
                <Message avatar={<Avatar initial={a.assignment.assigneeName.slice(0, 1)} seed={a.assignment.assigneeName} size={20} />} name={a.assignment.assigneeName} meta={a.assignment.completedAt ? timeCN(a.assignment.completedAt) : ''}>
                  {a.assignment.reply}
                </Message>
              </div>
            )}
            {a.assignment.returnReason && <div className="t-meta" style={{ marginTop: 8 }}>退回：{a.assignment.returnReason}</div>}
          </div>
        )}

        <div className="ib-sec">
          {replied || closed ? (
            <button type="button" className="ib-collapse" onClick={() => setShowEvidence(!showEvidence)}>
              <Icon name={showEvidence ? 'chevD' : 'chevR'} size={12} />
              <span>证据 · {a.evidence.length} 条日报原文</span>
            </button>
          ) : (
            <GroupLabel right={<span>{a.evidence.length} 条日报原文</span>}>证据</GroupLabel>
          )}
          {showEvidence && (
            <div className="ib-quotes">
              {a.evidence.length === 0 && <div className="t-meta">这段时间没有相关的日报原文</div>}
              {a.evidence.map((e, i) => (
                <Quote
                  key={i}
                  meta={`${e.memberName} · ${fmtDateWeek(e.date)} · ${e.source === 'report' ? '日报' : e.source === 'ai_reply' ? '回复 AI 追问' : '回复追问'}${e.revised ? ' · 原文已修改' : ''}`}
                >
                  {e.quote}
                </Quote>
              ))}
            </div>
          )}
        </div>

        <div className="ib-sec">
          {replied || closed ? (
            <button type="button" className="ib-collapse" onClick={() => setShowTimeline(!showTimeline)}>
              <Icon name={showTimeline ? 'chevD' : 'chevR'} size={12} />
              <span>时间线 · {a.timeline.length} 条</span>
            </button>
          ) : (
            <GroupLabel>时间线</GroupLabel>
          )}
          {showTimeline && (
            <div className="ib-tl">
              {a.timeline.map((t, i) => {
                const d = cnDate(new Date(t.at));
                const last = i === a.timeline.length - 1;
                return (
                  <div key={i} className={clsx('ib-tl-row', last && 'now')}>
                    <span className="dot" />
                    <span className="when">{d === today ? `今天 ${timeCN(t.at)}` : fmtMonthDay(d)}</span>
                    <span className="what">{t.text}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!closed && a.suggestions.length > 0 && (
          <div className="ib-sec">
            <GroupLabel>建议动作</GroupLabel>
            <div className="ib-sugs">
              {a.suggestions.map((s, i) => (
                <button key={s} type="button" className="ib-sug" onClick={() => onSuggestion(s)}>
                  <Kbd>{i + 1}</Kbd>
                  <span>{s}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Body>

      {!closed && (
        <Actions
          mobile={mobile}
          hints={
            <>
              <Hint keys={['R']}>追问</Hint>
              <Hint keys={['A']}>指派</Hint>
              <Hint keys={['E']}>已处理</Hint>
            </>
          }
        >
          {replied ? (
            <>
              <Button variant="primary" mobile={mobile} onClick={() => setModal({ type: 'resolve', a })}>
                标记已处理
              </Button>
              <Button mobile={mobile} onClick={() => setModal({ type: 'ask', a })}>
                继续追问
              </Button>
              <Button mobile={mobile} onClick={() => setModal({ type: 'assign', a })}>
                指派给…
              </Button>
            </>
          ) : (
            <>
              <Button variant="primary" mobile={mobile} onClick={() => setModal({ type: 'ask', a })}>
                追问负责人
              </Button>
              <Button mobile={mobile} onClick={() => setModal({ type: 'assign', a })}>
                指派给…
              </Button>
              <Button mobile={mobile} onClick={() => setModal({ type: 'resolve', a })}>
                标记已处理
              </Button>
            </>
          )}
        </Actions>
      )}
    </>
  );
}

function AssignmentDetail({ x, setModal, mobile }: { x: AssignmentDTO; setModal: (m: ModalState) => void; mobile: boolean }) {
  const { complete } = useInboxActions();
  const today = todayCN();
  const [reply, setReply] = useState('');
  const [error, setError] = useState<string | null>(null);
  const open = x.status === 'open';
  const send = async () => {
    if (!reply.trim()) return;
    try {
      await complete.mutateAsync({ id: x.id, reply });
      setReply('');
    } catch (e) {
      setError(errorText(e));
    }
  };
  useHotkeys({ 'mod+Enter': () => open && void send() }, !mobile && open);
  return (
    <>
      <Body mobile={mobile}>
        <div className="ib-kind">
          <Avatar initial={x.assignerInitial} seed={x.assignerName} size={16} />
          <span className="c2">{x.assignerName} 指派给你</span>
          <span>· {timeCN(x.createdAt)}</span>
          <span>
            · 期限 {fmtRelativeDay(cnDate(new Date(x.dueAt)), today)} {timeCN(x.dueAt)}
          </span>
        </div>
        <div className="t-page-title ib-title">{x.ask}</div>
        <div style={{ marginTop: 20 }}>
          <Message avatar={<Avatar initial={x.assignerInitial} seed={x.assignerName} size={20} />} name={x.assignerName} meta={timeCN(x.createdAt)}>
            {x.ask}
          </Message>
        </div>
        {x.background && (
          <div className="ib-sec">
            <GroupLabel right={<span>来自 {x.projectCode} {x.projectName}</span>}>背景</GroupLabel>
            <div className="t-body">{x.background}</div>
          </div>
        )}
        {x.reply && (
          <div className="ib-sec">
            <GroupLabel>你的回复</GroupLabel>
            <div className="t-body">{x.reply}</div>
          </div>
        )}
        {open && (
          <div className="ib-sec">
            <GroupLabel right={<span>· 会同时写入 {x.projectCode} 动态</span>}>回复{x.assignerName}</GroupLabel>
            <textarea className="wd-textarea" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="做了什么、结果是什么" aria-label="回复" />
            {error && <div className="t-meta c-red" style={{ marginTop: 6 }}>{error}</div>}
          </div>
        )}
      </Body>
      {open && (
        <Actions
          mobile={mobile}
          hints={
            <>
              <Hint keys={['⌘', '↵']}>完成</Hint>
              <Hint keys={['T']}>转交</Hint>
            </>
          }
        >
          <Button variant="primary" mobile={mobile} disabled={!reply.trim() || complete.isPending} onClick={() => void send()}>
            完成并回复
          </Button>
          <Button mobile={mobile} onClick={() => setModal({ type: 'transfer', x })}>
            转给他人
          </Button>
          <Button mobile={mobile} onClick={() => setModal({ type: 'return', x })}>
            无法完成…
          </Button>
        </Actions>
      )}
    </>
  );
}

function ExitDetail({ e, members, setModal, mobile }: { e: ExitRequestDTO; members: MemberDTO[]; setModal: (m: ModalState) => void; mobile: boolean }) {
  const { approveExit } = useInboxActions();
  const [handover, setHandover] = useState<string>(e.handoverToId ?? '');
  const [error, setError] = useState<string | null>(null);
  const pending = e.status === 'pending';
  const approve = async () => {
    try {
      await approveExit.mutateAsync({ id: e.id, planHandoverToId: handover || null });
    } catch (err) {
      setError(errorText(err));
    }
  };
  useHotkeys({ 'mod+Enter': () => pending && void approve() }, !mobile && pending);
  return (
    <>
      <Body mobile={mobile}>
        <div className="ib-kind">
          <Icon name="out" size={14} color="var(--text-3)" />
          <span className="c2">成员申请退出项目</span>
          <span>· 你是 {e.projectCode} 的负责人</span>
        </div>
        <div className="t-page-title ib-title">
          {e.memberName}申请退出 {e.projectCode} {e.projectName}
        </div>
        <div style={{ marginTop: 20 }}>
          <Message avatar={<Avatar initial={e.memberInitial} seed={e.memberName} size={20} />} name={e.memberName} meta={`${timeCN(e.createdAt)} · 来自日报`}>
            {e.reason}
          </Message>
        </div>
        {pending ? (
          <>
            <div className="ib-sec">
              <GroupLabel>同意后会发生什么</GroupLabel>
              <div className="ib-effects">
                <div>
                  <Icon name="check" size={14} color="var(--text-3)" />
                  <span>
                    {e.memberName}的日报从明天起不再预填 {e.projectCode}，也不会因此被追问
                  </span>
                </div>
                {e.recentReportCount > 0 && (
                  <div>
                    <Icon name="check" size={14} color="var(--text-3)" />
                    <span>
                      他过去 2 周在 {e.projectCode} 的 {e.recentReportCount} 条日报保留在项目动态里，只读
                    </span>
                  </div>
                )}
                {e.pendingPlans.length > 0 && (
                  <div>
                    <Icon name="check" size={14} color="var(--text-3)" />
                    <span>
                      他名下的 {e.pendingPlans.length} 项明日计划「{e.pendingPlans[0].text}」需要转给别人
                    </span>
                  </div>
                )}
              </div>
            </div>
            {e.pendingPlans.length > 0 && (
              <div className="row" style={{ gap: 12, marginTop: 16, fontSize: 13 }}>
                <span className="c4" style={{ width: 84 }}>
                  计划转给
                </span>
                <select className="ib-select" value={handover} onChange={(ev) => setHandover(ev.target.value)} aria-label="计划转给">
                  <option value="">不转交</option>
                  {members
                    .filter((m) => m.id !== e.memberId)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                </select>
                {e.handoverToName && <span className="t-meta">{e.memberName}提到的交接人</span>}
              </div>
            )}
            {error && <div className="t-meta c-red" style={{ marginTop: 8 }}>{error}</div>}
          </>
        ) : (
          <div className="ib-sec t-meta">{e.status === 'approved' ? '你已同意退出' : '你已暂不同意'}</div>
        )}
      </Body>
      {pending && (
        <Actions mobile={mobile} hints={<Hint keys={['⌘', '↵']}>同意</Hint>}>
          <Button variant="primary" mobile={mobile} disabled={approveExit.isPending} onClick={() => void approve()}>
            同意退出
          </Button>
          <Button mobile={mobile} onClick={() => setModal({ type: 'reject', e })}>
            暂不同意…
          </Button>
        </Actions>
      )}
    </>
  );
}

/** 发给我的追问（主管追问 / 之前日报上的 AI 追问） */
function FollowUpDetail({ f, mobile }: { f: FollowUpDTO; mobile: boolean }) {
  const { answer } = useInboxActions();
  const { data: me } = useMe();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = async (t: string) => {
    if (!t.trim()) return;
    try {
      await answer.mutateAsync({ id: f.id, answer: t });
      setText('');
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <>
      <Body mobile={mobile}>
        <div className="ib-kind">
          <span className="c2">
            {f.projectCode} {f.projectName}
          </span>
          {f.aboutDate && <span>· 关于你 {fmtMonthDay(f.aboutDate)} 的日报</span>}
        </div>
        <div style={{ marginTop: 16 }}>
          <Message avatar={f.kind === 'ai' ? <AIAvatar size={20} /> : <Avatar initial={f.fromInitial} seed={f.fromName} size={20} />} name={f.fromName} meta={timeCN(f.askedAt)}>
            {f.question}
          </Message>
        </div>
        {f.suggestedAnswers.length > 0 && (
          <div className="rp-choices" style={{ marginLeft: 24 }}>
            {f.suggestedAnswers.map((s, i) => (
              <button key={s} type="button" className="rp-choice" onClick={() => void send(s)}>
                <Kbd>{i + 1}</Kbd>
                <span>{s}</span>
              </button>
            ))}
          </div>
        )}
        <div className="ib-sec">
          <GroupLabel right={<span>· 回复会写进 {f.projectCode} 今天的进展</span>}>回复{f.kind === 'manager' ? f.fromName : ''}</GroupLabel>
          <textarea className="wd-textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={`${me?.name ?? ''}，直接写就好`} aria-label="回复" />
          {error && <div className="t-meta c-red" style={{ marginTop: 6 }}>{error}</div>}
        </div>
      </Body>
      <Actions mobile={mobile} hints={<Kbds keys={['⌘', '↵']} />}>
        <Button variant="primary" mobile={mobile} disabled={!text.trim() || answer.isPending} onClick={() => void send(text)}>
          回复
        </Button>
      </Actions>
    </>
  );
}

/** 不在工作区的人申请加入（设计稿 25e 的管理员一侧） */
function JoinDetail({ j, mobile }: { j: JoinRequestDTO; mobile: boolean }) {
  const { data: me } = useMe();
  const [name, setName] = useState(j.email.split('@')[0]);
  const [teamId, setTeamId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const act = async (path: 'approve' | 'ignore') => {
    setBusy(true);
    try {
      await post(`/join-requests/${j.id}/${path}`, path === 'approve' ? { name, teamId: teamId || null } : {});
      await queryClient.invalidateQueries({ queryKey: ['inbox'] });
      await queryClient.invalidateQueries({ queryKey: ['members'] });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Body mobile={mobile}>
        <div className="ib-kind">
          <Icon name="user" size={14} color="var(--text-3)" />
          <span className="c2">申请加入工作区</span>
          <span>· {fmtListTime(j.createdAt, todayCN())}</span>
        </div>
        <div className="t-page-title ib-title">{j.email}</div>
        <div className="t-body" style={{ marginTop: 12 }}>
          这个邮箱还不在日报工作区里，对方在登录页点了「请管理员邀请我」。加入后，对方用这个邮箱登录，第一次会先选择负责的项目。
        </div>
        <div className="ib-sec col" style={{ gap: 10, maxWidth: 360 }}>
          <label className="col" style={{ gap: 6 }}>
            <span className="t-group">姓名</span>
            <input className="wd-input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="col" style={{ gap: 6 }}>
            <span className="t-group">团队</span>
            <select className="ib-select" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">暂不分配</option>
              {me?.teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          {error && <div className="t-meta c-red">{error}</div>}
        </div>
      </Body>
      <Actions mobile={mobile}>
        <Button variant="primary" mobile={mobile} disabled={busy || !name.trim()} onClick={() => void act('approve')}>
          加入工作区
        </Button>
        <Button mobile={mobile} disabled={busy} onClick={() => void act('ignore')}>
          忽略
        </Button>
      </Actions>
    </>
  );
}

function MessageDetail({ msg, mobile }: { msg: MemberMessageDTO; mobile: boolean }) {
  const read = useMessageRead();
  useEffect(() => {
    if (!msg.read) read.mutate(msg.id);
  }, [msg.id]);
  return (
    <Body mobile={mobile}>
      <div className="ib-kind">
        <span className="c2">{msg.kind === 'thanks' ? '感谢' : '约个 1:1'}</span>
        <span>· {fmtListTime(msg.createdAt, todayCN())}</span>
      </div>
      {msg.kind === 'one_on_one' && msg.meetingAt && (
        <div className="t-page-title ib-title">
          {fmtDateWeek(cnDate(new Date(msg.meetingAt)))} {timeCN(msg.meetingAt)}
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <Message avatar={<Avatar initial={msg.from.initial} seed={msg.from.name} size={20} />} name={msg.from.name} meta={timeCN(msg.createdAt)}>
          {msg.text}
        </Message>
      </div>
      {msg.kind === 'one_on_one' && <div className="t-meta" style={{ marginTop: 16, marginLeft: 32 }}>时间不合适可以直接回复{msg.from.name}的微信，另约一个。</div>}
    </Body>
  );
}

function HandoverDetail({ h, mobile }: { h: HandoverDTO; mobile: boolean }) {
  const navigate = useNavigate();
  const { accept, decline } = useHandoverActions();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const busy = accept.isPending || decline.isPending;
  const done = { onError: (e: unknown) => setError(errorText(e)) };
  return (
    <>
      <Body mobile={mobile}>
        <div className="ib-kind">
          <Icon name="refresh" size={14} color="var(--text-3)" />
          <span className="c2">离职交接</span>
          <span>· {h.memberName}最后工作日 {fmtDateWeek(h.lastDay)}</span>
        </div>
        <div className="t-page-title ib-title">
          请确认接手 {h.projectCode} {h.projectName}
        </div>
        <div className="t-body" style={{ marginTop: 12 }}>
          {h.memberName}即将离职，管理员请你接手这个项目。确认后你会成为项目负责人：进行中的指派转给你，之后的日报会按这个项目预填。历史日报保留在项目动态里，署名不变。
        </div>
        <div className="ib-sec">
          <Button variant="text" onClick={() => navigate(`/projects/${h.projectId}`)}>
            先看看项目动态 ›
          </Button>
        </div>
        {declining && (
          <div className="ib-sec col" style={{ gap: 8, maxWidth: 420 }}>
            <span className="t-group">无法接手的原因</span>
            <input className="wd-input" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="例如：手上项目在冲量产，排不开" />
            <span className="t-meta">管理员会看到这句话，然后换一个人。</span>
          </div>
        )}
        {error && <div className="t-meta c-red" style={{ marginTop: 8 }}>{error}</div>}
      </Body>
      <Actions mobile={mobile}>
        {declining ? (
          <>
            <Button variant="primary" mobile={mobile} disabled={busy || !reason.trim()} onClick={() => decline.mutate({ id: h.id, reason }, done)}>
              提交
            </Button>
            <Button mobile={mobile} onClick={() => setDeclining(false)}>
              取消
            </Button>
          </>
        ) : (
          <>
            <Button variant="primary" mobile={mobile} disabled={busy} onClick={() => accept.mutate(h.id, done)}>
              确认接手
            </Button>
            <Button mobile={mobile} disabled={busy} onClick={() => setDeclining(true)}>
              无法接手
            </Button>
          </>
        )}
      </Actions>
    </>
  );
}

function MentionDetail({ m, mobile }: { m: MentionDTO; mobile: boolean }) {
  const navigate = useNavigate();
  const open = () => navigate(`/projects/${m.projectId}`);
  useHotkeys({ Enter: open }, !mobile);
  return (
    <>
      <Body mobile={mobile}>
        <div className="ib-kind">
          <span className="c2">
            {m.projectCode} {m.projectName}
          </span>
          <span>· {m.author.name}在项目动态里提到你</span>
        </div>
        <div style={{ marginTop: 16 }}>
          <Message avatar={<Avatar initial={m.author.initial} seed={m.author.name} size={20} />} name={m.author.name} meta={fmtListTime(m.createdAt, todayCN())}>
            <RichText text={m.text} />
          </Message>
        </div>
      </Body>
      <Actions mobile={mobile} hints={<Hint keys={['↵']}>打开项目</Hint>}>
        <Button variant="primary" mobile={mobile} onClick={open}>
          打开项目回复
        </Button>
      </Actions>
    </>
  );
}

function AllDone({ inbox }: { inbox: InboxDTO }) {
  const navigate = useNavigate();
  const rechecks = inbox.anomalies.filter((a) => a.resolution?.kind === 'following').length;
  const total = inbox.anomalies.length;
  return (
    <>
      <TopBar crumbs={['收件箱']} />
      <EmptyState
        icon="check"
        title={total > 0 ? `今天的 ${total} 条都处理完了` : inbox.mentions.length ? '没有需要你处理的事' : '收件箱是空的'}
        desc={
          total > 0
            ? `下次检查 ${fmtRelativeDay(cnDate(new Date(inbox.nextCheck)), todayCN())} ${timeCN(inbox.nextCheck)}${rechecks ? ` · ${rechecks} 条设了到期复查` : ''}`
            : inbox.mentions.length
              ? '左侧是最近 7 天在项目动态里提到你的评论。'
              : '有人追问你、指派给你，或者项目出现异常时，会出现在这里。'
        }
      >
        <Button onClick={() => navigate('/radar')}>打开项目雷达</Button>
        <Kbds keys={['G', 'R']} />
      </EmptyState>
    </>
  );
}

function MobileList({ inbox, entries }: { inbox: InboxDTO; entries: Entry[] }) {
  const navigate = useNavigate();
  const today = todayCN();
  return (
    <div style={{ flex: 1, overflow: 'auto' }}>
      <div className="ibm-head">
        <div className="t-page-title">收件箱</div>
        <div className="t-meta" style={{ marginTop: 6 }}>
          {inbox.counts.pending > 0 ? `${inbox.counts.pending} 条待处理` : '没有待处理的事'}
          {inbox.counts.handled > 0 ? ` · ${inbox.counts.handled} 条已处理` : ''}
        </div>
      </div>
      {entries.length === 0 && <EmptyState icon="inbox" title="收件箱是空的" desc="有人追问你、指派给你时，会出现在这里。" />}
      {entries.map((en) => (
        <button key={en.id} type="button" className={clsx('ibm-row', isDone(en) && 'done')} onClick={() => navigate(`/inbox/${en.id}`)}>
          {entryIcon(en)}
          <span className="info">
            <span className="t">{entryTitle(en)}</span>
            <span className="s">
              {en.kind === 'anomaly'
                ? `${en.a.involvedNames.join(' · ')} · ${fmtListTime(en.a.generatedAt, today)}`
                : en.kind === 'assignment'
                  ? `${en.x.assignerName} · 期限 ${fmtRelativeDay(cnDate(new Date(en.x.dueAt)), today)} ${timeCN(en.x.dueAt)}`
                  : en.kind === 'exit'
                    ? `${en.e.memberName} · ${fmtListTime(en.e.createdAt, today)}`
                    : en.kind === 'join'
                      ? `申请加入 · ${fmtListTime(en.j.createdAt, today)}`
                      : en.kind === 'mention'
                        ? `${en.m.author.name}在 ${en.m.projectCode} 提到你 · ${fmtListTime(en.m.createdAt, today)}`
                        : en.kind === 'handover'
                          ? `${en.h.memberName}离职交接 · ${fmtListTime(en.h.createdAt, today)}`
                          : en.kind === 'message'
                            ? `${en.msg.from.name} · ${fmtListTime(en.msg.createdAt, today)}`
                          : `${en.f.fromName} · ${fmtListTime(en.f.askedAt, today)}`}
            </span>
          </span>
          <Icon name="chevR" size={14} />
        </button>
      ))}
    </div>
  );
}
